import './App.css'

import { FirebaseApp } from 'firebase/app'
import { getMessaging, isSupported, onMessage } from 'firebase/messaging'
import { getPerformance } from 'firebase/performance'
import React, { useEffect } from 'react'

import { Analytics } from './Analytics'
import { useFirebaseApp } from './firebase/FirebaseProvider'
import { ErrorBoundary } from './Game/components/ErrorBoundary'
import { ToastProvider } from './Game/components/Toast'
import { Router } from './Router'
import { ThemeProvider } from './theme/ThemeProvider'

export function useForegroundMessages(firebaseApp: FirebaseApp) {
  useEffect(() => {
    getPerformance(firebaseApp)

    let unsubscribe: (() => void) | undefined
    let cancelled = false

    // `getMessaging` throws synchronously in environments that lack FCM
    // support (notably non-secure contexts, where `navigator.serviceWorker`
    // is undefined). Gate it behind the async `isSupported()` check so an
    // unsupported environment degrades quietly instead of crashing <App />.
    void isSupported()
      .then((supported) => {
        if (cancelled || !supported) return
        const messaging = getMessaging(firebaseApp)
        unsubscribe = onMessage(messaging, () => {
          // foreground message received
        })
      })
      .catch(() => {
        // Messaging unavailable in this environment; nothing to subscribe to.
      })

    return () => {
      cancelled = true
      unsubscribe?.()
    }
  }, [firebaseApp])
}

function App() {
  const firebaseApp = useFirebaseApp()

  useForegroundMessages(firebaseApp)

  return (
    <ThemeProvider>
      <ToastProvider>
        <ErrorBoundary>
          <Router />
        </ErrorBoundary>
        <Analytics />
      </ToastProvider>
    </ThemeProvider>
  )
}

export default App
